/**
 * Sheet resolution for a catalog section's exploded view.
 *
 * A section ships several drawings and each carries a different slice of the
 * parts. On 32/335 (CENTRAL BEARING, REAR AXLE CONTROL ARM) sheet 1 holds
 * callouts 10-110 — the triangular upper wishbones — and sheet 2 holds 200-310
 * — the longitudinal lower arms. A viewer that renders one sheet therefore
 * hides half the answers to "which type of control arm do you need?".
 *
 * Kept pure and outside the component so the rules below are testable without
 * a React runtime.
 */

export interface SheetCalloutBox {
  label: string;
  bounds: { x: number; y: number; w: number; h: number };
  normalized?: boolean;
}

export interface Sheet {
  index: number;
  id?: string | null;
  /** Per-sheet identity, from the drawing id. NOT the group·section code, which
   *  is the same on every sheet of a section. */
  code?: string | null;
  diagram_url: string | null;
  callouts: SheetCalloutBox[];
  part_callouts?: string[];
}

export interface SheetSource {
  diagram_url: string | null;
  callouts: SheetCalloutBox[];
  sheets?: Sheet[] | null;
  primary_sheet?: number | null;
}

/** Normalise a payload to sheets. Pre-multi-sheet payloads carry one image, so
 *  they become a single synthetic sheet and callers stay sheet-agnostic. */
export function sheetsFrom(source: SheetSource | null | undefined): Sheet[] {
  if (!source) return [];
  if (source.sheets && source.sheets.length) return source.sheets;
  return [{ index: 1, diagram_url: source.diagram_url, callouts: source.callouts || [] }];
}

/** callout label -> the first sheet that draws it. */
export function sheetOfCalloutMap(sheets: Sheet[]): Map<string, number> {
  const map = new Map<string, number>();
  sheets.forEach(sheet => {
    const labels = sheet.part_callouts && sheet.part_callouts.length
      ? sheet.part_callouts
      : (sheet.callouts || []).map(c => String(c.label));
    labels.forEach(label => {
      const key = String(label);
      if (!map.has(key)) map.set(key, sheet.index);
    });
  });
  return map;
}

export interface SheetSeekRow {
  callout: string;
  candidate?: boolean;
  score?: number | null;
  sheet?: number | null;
}

/** The sheet a row is drawn on: its own `sheet`, else the sheet carrying its
 *  callout, else the payload's primary. */
export function sheetOfRow(
  row: SheetSeekRow,
  byCallout: Map<string, number>,
  fallback: number,
): number {
  if (row.sheet != null) return row.sheet;
  const found = byCallout.get(String(row.callout));
  return found != null ? found : fallback;
}

/**
 * Which sheet to open on: the one holding the best candidate.
 *
 * Opening on sheet 1 is wrong whenever the answers live elsewhere — on the
 * reference case every arm being compared is on sheet 2, so sheet 1 shows a
 * drawing with none of them and the question looks unanswerable.
 */
export function openingSheet(
  sheets: Sheet[],
  rows: SheetSeekRow[],
  primary?: number | null,
): number {
  if (!sheets.length) return 1;
  const fallback = primary != null && sheets.some(s => s.index === primary)
    ? primary
    : sheets[0].index;
  const byCallout = sheetOfCalloutMap(sheets);
  const best = rows
    .filter(r => r.candidate)
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))[0];
  if (!best) return fallback;
  const target = sheetOfRow(best, byCallout, fallback);
  return sheets.some(s => s.index === target) ? target : fallback;
}
